import React, { useContext, useEffect } from 'react';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Close from '@material-ui/icons/Close';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useIsMobile } from '../../../../util/style-utils';
import MultiplePremiseMobileView from './multiplePremiseMobileView';
import {
  OutageLookupParams,
  OutageLookupResponse,
  AccountDetail,
  PremiseInfo,
} from '../../../../__generated__/pge-types';
import { useOutageLookUp } from '../hooks/useOutageLookUp';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import MultiplePremiseWebView from './multiplePremiseWebView';
import useOutageCenterUtils from '../utils';
import useGTMUtil from '../../../../lib/useGTMUtil';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      display: 'flex',
      width: '922px',
      maxHeight: '77.21vh',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: theme.spacing(3.5, 3, 6.25, 3),
      backgroundColor: colors.modalBackgroundColor,
      overflow: 'auto',
      borderRadius: '5px',
      [theme.breakpoints.down('sm')]: {
        top: '50%',
        width: '90%',
        maxHeight: '90%',
        padding: theme.spacing(3),
      },
    },
    closeButton: {
      position: 'absolute',
      top: 6,
      right: 24,
    },
  }),
);

type Props = {
  open: boolean;
  onClose: () => void;
  outageDetailData: OutageLookupResponse;
  outageLookUpResult: (
    outageLookUpResult: OutageLookupResponse,
    accountNumber: string | undefined,
    accountIdentifier: string | undefined,
    houseNumber: string | undefined,
    encryptedPremiseId: string | undefined,
  ) => void;
  reportOutageFromIcon: boolean;
  setSelectedEncryptedPremiseId?: (
    encryptedPremiseId: string | null | undefined,
  ) => void;
  outageFindAccount?: boolean;
  directlySetPremise: boolean;
  selectedAccount: AccountDetail | null;
  setPremise: (
    accountDetail: AccountDetail,
    premise: PremiseInfo,
    hasKnownOutage: boolean,
  ) => void;
};

const MultiplePremise: React.FC<Props> = ({
  open,
  onClose,
  outageDetailData,
  outageLookUpResult,
  reportOutageFromIcon,
  setSelectedEncryptedPremiseId,
  outageFindAccount,
  directlySetPremise,
  selectedAccount,
  setPremise,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { getOutageLookUpQueryResponse } = useOutageLookUp();

  const notificationContext = useContext(NotificationsContext);
  const { setState: updateTransition } = useContext(TransitionContext);
  const { setErrorNotification } = useOutageCenterUtils();
  const { postGTMModalVisibilityData } = useGTMUtil();

  //Push modal visibility status to the GTM
  useEffect(() => {
    const GTMPageData = {
      page: 'Multiple Premise',
      siteSection: 'Multiple Premise Modal',
    };

    postGTMModalVisibilityData({ ...GTMPageData, isVisible: true });

    return () =>
      postGTMModalVisibilityData({ ...GTMPageData, isVisible: false });
  }, []);

  const showError = () => {
    setErrorNotification(true);
    updateTransition({ open: false });
    onClose();
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const getOutageDetailSumbit = async (
    OutageLookupParamsValues: OutageLookupParams,
    houseNumber: string | undefined = undefined,
    premise: PremiseInfo | null,
  ) => {
    if (directlySetPremise) {
      const hasKnownOutage = Boolean(
        premise?.servicePointDetails?.find(point => {
          return point?.outageInfo?.hasKnownOutage;
        }),
      );
      if (selectedAccount && premise) {
        setPremise(selectedAccount, premise, hasKnownOutage);
      }
    } else {
      try {
        setSelectedEncryptedPremiseId &&
          setSelectedEncryptedPremiseId(
            OutageLookupParamsValues?.encryptedValue,
          );
        await getOutageDetail(OutageLookupParamsValues, houseNumber);
      } catch {
        showError();
      }
    }
  };

  const getOutageDetail = async (
    params: OutageLookupParams,
    houseNumber: string | undefined = undefined,
  ) => {
    updateTransition({ open: true });
    try {
      const getOutageLookupResponse = await getOutageLookUpQueryResponse(
        params,
      );
      if (
        Boolean(getOutageLookupResponse) &&
        getOutageLookupResponse?.isEligibleToReport !== null &&
        getOutageLookupResponse?.isEligibleToReport !== undefined
      ) {
        outageLookUpResult(
          getOutageLookupResponse,
          undefined,
          undefined,
          houseNumber,
          params.encryptedValue || '',
        );
        updateTransition({ open: false });
        onClose;
      } else {
        showError();
      }
    } catch {
      showError();
    }
  };

  return isMobile ? (
    <MultiplePremiseMobileView
      open={open}
      onClose={onClose}
      outageDetailData={outageDetailData}
      selectedPremiseParams={getOutageDetailSumbit}
      reportOutageFromIcon={reportOutageFromIcon}
      outageFindAccount={outageFindAccount}
    />
  ) : (
    <Modal
      aria-labelledby="report-outage-modal"
      aria-describedby="report-outage-modal"
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <div className={classes.modalWrapper}>
        <div className={classes.closeButton}>
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={onClose}
            data-testid="close-icon"
          >
            <Close />
          </IconButton>
        </div>
        <Grid>
          <MultiplePremiseWebView
            open={open}
            onClose={onClose}
            outageDetailData={outageDetailData}
            selectedPremiseParams={getOutageDetailSumbit}
          />
        </Grid>
      </div>
    </Modal>
  );
};

export default MultiplePremise;
